// src/rms/components/PinGate.jsx
// ============================================================================
// A section that opens on a PIN, and shuts again on Escape
// ============================================================================
// Wraps a whole screen. Until somebody signs, the children DO NOT MOUNT -- this
// returns the modal and nothing else.
//
// That distinction is the reason this is a wrapper rather than an overlay. A
// modal painted over a rendered page is a curtain: the rows are already in the
// DOM, the requests already went out, and devtools reads straight through it.
// Here the children never render, so their useCollection hooks never fire and
// the rows never arrive.
//
// Be honest about what that is and is not. It is not the same guarantee as
// Reports, where rms_gl_entries has no SELECT grant and the server refuses. RLS
// still permits these reads for any member; what has changed is that the
// application does not make them. Somebody with devtools and the anon key can
// still query.
//
// ----------------------------------------------------------------------------
// The PIN decides, not the session
// ----------------------------------------------------------------------------
// A counter terminal signs in once, as a staff-level member, and stays signed
// in all day. So the session's role is the wrong thing to ask when an owner
// walks up: the question is whose PIN was typed.
//
// rms_elevate answers it server-side and hands back who they are. Those
// capabilities are published to the subtree through useElevation(), and the
// screens inside read THAT rather than the session's -- otherwise an owner
// would open Settings on a staff terminal and find the tabs missing.
//
// What comes back is an identity, never a credential. It grants no database
// rights; RLS is unchanged and still keyed on auth.uid(). Every write behind
// one of these screens still has to be permitted on its own terms, which is
// why the writes are moving into functions that take the PIN themselves.
//
// ----------------------------------------------------------------------------
// Escape re-locks
// ----------------------------------------------------------------------------
// An owner changes something, then wants the screen shut before they walk away.
// Escape does it: the children unmount, their data goes with them, and the
// prompt comes back.
//
// Which means Escape DISCARDS an unsaved edit. That is the intended trade --
// re-locking that left a half-filled form on screen would not be re-locking --
// but it is why the banner says what the key does rather than leaving somebody
// to find out by losing work.
// ============================================================================

import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRms } from "../context/RmsContext";
import pinService from "../data/pinService";
import { capabilitiesFor } from "../data/permissions";
import PinModal from "./PinModal";

const ElevationContext = createContext(null);

/**
 * Who opened this section, and what they may do.
 *
 * Falls back to the session's own capabilities outside a gate, so a screen can
 * be rendered both ways without branching -- Settings behind the gate, a future
 * embedded panel not.
 */
export function useElevation() {
  const ctx = useContext(ElevationContext);
  const rms = useRms();
  return ctx || { elevated: false, member: null, role: rms.role, can: rms.can, firstName: null };
}

export default function PinGate({ action, section, title, explain, children }) {
  const { operator, t } = useRms();
  const navigate = useNavigate();

  // The section name is the vocabulary rms_elevate speaks; `action` is kept for
  // the caller's readability and is derived rather than trusted.
  const name = section || String(action || "").replace(/\.open$/, "");

  const [who, setWho] = useState(null);

  const unlock = useCallback(
    async (pin) => {
      const result = await pinService.elevate(operator, pin, name);
      // The server has already resolved the PIN to a member and checked that
      // member's role. A refusal arrives here as its message.
      if (!result.success) return { error: result.error };
      setWho(result.data);
      return {};
    },
    [operator, name]
  );

  useEffect(() => {
    if (!who) return undefined;
    const onKey = (e) => {
      // Only the bare key. Escape inside a native picker or an IME composition
      // arrives with isComposing set, and re-locking the screen because
      // somebody dismissed a date picker would be maddening.
      if (e.key === "Escape" && !e.isComposing) setWho(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [who]);

  if (!who) {
    return (
      <PinModal
        title={title}
        explain={explain}
        onCancel={() => navigate("/rms")}
        onResolved={unlock}
      />
    );
  }

  const value = {
    elevated: true,
    member: who.member_id,
    role: who.role,
    firstName: who.first_name,
    can: capabilitiesFor(who.role),
  };

  return (
    <ElevationContext.Provider value={value}>
      <div className="rms-pin-unlocked">
        <span>
          {who.first_name
            ? t("pinGate.unlockedBy", "Opened by {name}.", { name: who.first_name })
            : t("pinGate.unlocked", "Unlocked with your PIN.")}
        </span>
        <button type="button" className="rms-linkish" onClick={() => setWho(null)}>
          {t("pinGate.lockNow", "Lock this screen (Esc)")}
        </button>
      </div>
      {children}
    </ElevationContext.Provider>
  );
}
